import { ComponentProps, useId } from 'react'
import * as Sentry from '@sentry/nextjs'
import { toastManager } from '@systemeio/components'
import { Microphone01Icon } from '@systemeio/icons'
import { useTranslation } from 'next-i18next'
import { twMerge } from 'tailwind-merge'
import {
  AUDIO_SIZE_LIMIT_BYTES,
  AUDIO_SIZE_LIMIT_MB,
  AudioAttachmentMIMEType,
} from 'shared/constants'
import { usePostAttachmentsContext } from 'modules/attachments/components/context'
import { useMultipleFileUpload } from 'modules/attachments/hooks/use-multiple-file-upload'
import useUploadFile from 'modules/attachments/hooks/use-upload-datafile'
import {
  AttachmentTypeEnum,
  AttachmentUploadStatusEnum,
} from 'modules/community/types/post-attachment'

export interface AudioAttachmentProps extends ComponentProps<'input'> {
  className?: string
  iconClassName?: string
}

export const AudioAttachment = ({
  className,
  iconClassName,
  id: inputId,
  onChange,
  ...props
}: AudioAttachmentProps): React.ReactNode => {
  const { t } = useTranslation()
  const generatedId = useId()

  const { addError, addAttachment, updateAttachment, limit } = usePostAttachmentsContext()
  const { validateAndGetUploadOptions, uploadFile, activateFile } = useUploadFile()
  const { handleMultipleFileUpload } = useMultipleFileUpload()

  const handleAudioUpload = async (file: File): Promise<void> => {
    if (file.size > AUDIO_SIZE_LIMIT_BYTES) {
      toastManager.add({
        type: 'error',
        title: t('post.attachments.file_upload.size_limit_reached', {
          size: `${AUDIO_SIZE_LIMIT_MB}MB`,
        }),
      })
      return
    }

    const attachmentId = crypto.randomUUID()

    addAttachment({
      id: attachmentId,
      status: AttachmentUploadStatusEnum.Uploading,
      type: AttachmentTypeEnum.Audio,
    })

    try {
      await limit(async () => {
        const { id, uploadOptions } = await validateAndGetUploadOptions({
          file,
        })
        await uploadFile({ file, uploadOptions })
        const activatedFile = await activateFile(id)

        updateAttachment(attachmentId, {
          name: activatedFile[id].name,
          dataFileId: id,
          status: AttachmentUploadStatusEnum.Success,
          url: activatedFile[id].path,
        })
      })
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          context: 'Failed to upload audio attachment',
        },
      })
      addError(attachmentId, t('post.attachments.upload_failed'))
    }
  }

  const handleSelectFile = async (file: File): Promise<void> => {
    await handleAudioUpload(file)
  }

  const handleSelectFiles = async (files: File[]): Promise<void> => {
    await handleMultipleFileUpload(files, handleSelectFile)
  }

  const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    onChange?.(e)

    const files = [...(e.target.files ?? [])]

    if (files.length > 0) {
      const validFiles: File[] = []
      const invalidFiles: File[] = []

      for (const file of files) {
        if (
          AudioAttachmentMIMEType.split(',')
            .map(type => type.trim())
            .includes(file.type)
        ) {
          validFiles.push(file)
          continue
        }
        invalidFiles.push(file)
      }

      if (invalidFiles.length === 1) {
        toastManager.add({
          type: 'error',
          title: t('post.attachments.file_upload.unsupported_type', {
            fileName: invalidFiles[0].name,
          }),
        })
      } else if (invalidFiles.length > 1) {
        toastManager.add({
          type: 'error',
          title: t('post.attachments.file_upload.unsupported_types_count', {
            count: invalidFiles.length,
          }),
        })
      }

      if (validFiles.length > 0) {
        await handleSelectFiles(validFiles)
      }
    }

    // NOTE: Reset value to have the ability to open the same file https://stackoverflow.com/questions/4109276/how-to-detect-input-type-file-change-for-the-same-file
    e.target.value = ''
  }

  const id = inputId ?? generatedId

  return (
    <label htmlFor={id} className={twMerge('cursor-pointer font-medium', className)}>
      <input
        {...props}
        type="file"
        className="invisible absolute h-1 w-0"
        onChange={handleInputChange}
        accept={AudioAttachmentMIMEType}
        multiple
        id={id}
      />
      <span className="sr-only">{t('global.upload_audio')}</span>
      <Microphone01Icon className={twMerge('size-6 text-text-primary', iconClassName)} />
    </label>
  )
}
